import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Display details (name, photo, company) for people the caller may not read directly.
 * Portal clients can only read their own company's `users` docs (firestore.rules), so
 * Community authors and Messages participants from other companies come from the
 * getPublicProfiles callable (functions/portalDirectory.js).
 *
 * Each id may be a login uid (staff or portal) or a client account id (resolved to the
 * account's primary portal user). A profile looks like:
 *   { id, type: 'internal' | 'portal' | 'client', firstName, lastName, name, profileImage,
 *     company, accountId }
 * Lookups made in the same tick go out as one call; results are cached for the session.
 */
const getPublicProfilesFn = httpsCallable(functions, 'getPublicProfiles');

const cache = new Map(); // id -> Promise<profile|null>
let pending = null; // { ids: Set, resolvers: Map<id, fn> }

const MAX_IDS_PER_CALL = 100; // matches the callable's cap

function flush() {
  const { ids, resolvers } = pending;
  pending = null;
  const all = [...ids];
  for (let i = 0; i < all.length; i += MAX_IDS_PER_CALL) {
    const chunk = all.slice(i, i + MAX_IDS_PER_CALL);
    getPublicProfilesFn({ ids: chunk })
      .then(({ data }) => {
        const profiles = data?.profiles || {};
        chunk.forEach((id) => resolvers.get(id)(profiles[id] || null));
      })
      .catch((error) => {
        console.error('Error loading public profiles:', error);
        chunk.forEach((id) => {
          cache.delete(id); // allow a retry later
          resolvers.get(id)(null);
        });
      });
  }
}

/** @param {string} id @returns {Promise<Object|null>} */
export function fetchPublicProfile(id) {
  if (!id || typeof id !== 'string') return Promise.resolve(null);
  if (cache.has(id)) return cache.get(id);
  if (!pending) {
    pending = { ids: new Set(), resolvers: new Map() };
    setTimeout(flush, 0);
  }
  const promise = new Promise((resolve) => {
    pending.ids.add(id);
    pending.resolvers.set(id, resolve);
  });
  cache.set(id, promise);
  return promise;
}

/** @param {string[]} ids @returns {Promise<Object>} map of id -> profile (missing ids omitted) */
export async function fetchPublicProfiles(ids) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  const results = await Promise.all(unique.map((id) => fetchPublicProfile(id)));
  const out = {};
  unique.forEach((id, i) => {
    if (results[i]) out[id] = results[i];
  });
  return out;
}

/** Two-letter avatar initials for a profile. */
export function profileInitials(profile) {
  const first = profile?.firstName || '';
  const last = profile?.lastName || '';
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.substring(0, 2).toUpperCase();
  return String(profile?.name || profile?.company || 'U').substring(0, 2).toUpperCase();
}
